import { CoverageTierEnum } from "@platform/openapi-types";

import type { Rate, RateSchedule } from "../types";

export const getRateExposure = (rate: Rate): number =>
  rate.volume ?? rate.numberOfLives ?? 0;

export const getRateDivisor = (unit?: string): number => {
  switch (unit) {
    case "volumePer10":
    case "per10OfSalary":
      return 10;
    case "volumePer100":
    case "per100OfSalary":
      return 100;
    case "volumePer1000":
    case "per1000OfSalary":
      return 1000;
    default:
      return 1;
  }
};

const getPrimaryRatesForAdndRate = (
  adndRate: Rate,
  primarySchedule?: RateSchedule,
): Rate[] => {
  if (!primarySchedule?.rates?.length) {
    return [];
  }

  const primaryRates = primarySchedule.rates;

  if (!adndRate.coverageTierCode) {
    const employeeTierRates = primaryRates.filter(
      (rate) => rate.coverageTierCode === CoverageTierEnum.employee,
    );

    return employeeTierRates.length > 0
      ? employeeTierRates
      : primaryRates.filter((rate) => !rate.coverageTierCode);
  }

  return primaryRates.filter(
    (rate) => rate.coverageTierCode === adndRate.coverageTierCode,
  );
};

const computeBlendedRate = (
  rates: Rate[],
  fallbackUnit?: string,
): number | undefined => {
  const weighted = rates.reduce(
    (acc, rate) => {
      if (rate.rate == null) {
        return acc;
      }

      const normalizedExposure =
        getRateExposure(rate) / getRateDivisor(rate.unit ?? fallbackUnit);

      if (normalizedExposure === 0) {
        return acc;
      }

      return {
        premium: acc.premium + rate.rate * normalizedExposure,
        exposure: acc.exposure + normalizedExposure,
      };
    },
    { premium: 0, exposure: 0 },
  );

  if (weighted.exposure === 0) {
    return undefined;
  }

  return weighted.premium / weighted.exposure;
};

export const getAdndExposureFromPrimarySchedule = (
  adndRate: Rate,
  primarySchedule: RateSchedule,
): number =>
  getPrimaryRatesForAdndRate(adndRate, primarySchedule).reduce(
    (sum, rate) => sum + getRateExposure(rate),
    0,
  );

export const resolveAdndRateFromPrimarySchedule = (
  adndRate: Rate,
  primarySchedule?: RateSchedule,
): number | undefined => {
  if (adndRate.rate != null && adndRate.rate !== 0) {
    return adndRate.rate;
  }

  const primaryRates = getPrimaryRatesForAdndRate(adndRate, primarySchedule);
  if (primaryRates.length === 0) {
    return adndRate.rate;
  }

  if (primaryRates.length === 1) {
    return primaryRates[0].rate ?? adndRate.rate;
  }

  return computeBlendedRate(primaryRates, adndRate.unit) ?? adndRate.rate;
};
